import React from 'react';
import { DialogField, DialogOption, ConditionOption, DialogVariable } from '@/lib/stores/dialogStore';
import { DialogTextFieldEditor } from './field-editors/DialogTextFieldEditor';
import { QuestionFieldEditor } from './field-editors/QuestionFieldEditor';
import { ConditionFieldEditor } from './field-editors/ConditionFieldEditor';
import { SetterFieldEditor } from './field-editors/SetterFieldEditor';
import { EndFieldEditor } from './field-editors/EndFieldEditor';
import { ExternalDialogFieldEditor } from './field-editors/ExternalDialogFieldEditor';

interface DialogFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[]; // Added for friendly names
  currentSectionFields?: DialogField[]; // Fields in the current section for "to field" navigation
  currentDialogId?: string; // Current dialog ID to exclude from external dialog list
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  onAddOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  onAddConditionOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  onRemoveConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  gameId?: string;
  t: any;
}

/**
 * DialogFieldEditor - Router component that delegates to specialized field editors
 * based on the field type. This maintains backward compatibility while organizing
 * the code into more manageable, specialized components.
 */
export const DialogFieldEditor = React.memo(function DialogFieldEditor(props: DialogFieldEditorProps) {
  const { field } = props;

  // Route to appropriate specialized editor based on field type
  switch (field.type) {
    case 'dialog':
    case 'context':
      return <DialogTextFieldEditor {...props} />;
    
    case 'question':
      return <QuestionFieldEditor {...props} />;
    
    case 'condition':
      return <ConditionFieldEditor {...props} />;
    
    case 'setter':
      return <SetterFieldEditor {...props} />;
    
    case 'external-dialog':
      return <ExternalDialogFieldEditor {...props} />;
    
    case 'end-dialog':
    case 'end-text':
      return <EndFieldEditor {...props} />;
    
    default:
      // Default to dialog text editor for any unknown types
      return <DialogTextFieldEditor {...props} />;
  }
});

